/**
 * The Reach's one-off pipeline warm-up (M3 plan R20), started on the first rendered frame in reach mode.
 *
 * On a first run every new material set compiled on its first real draw: 0.3–3.4 s freezes when the first raider came
 * into view, at the first kill (the effects pools) and at the start of the first jump (the tunnel), measured headless
 * in R20. The boot warm-ups of the effects and the tunnel compile outside the post pipeline's passes, so their
 * pipelines never match (see src/render/reachFx/warmup.ts).
 *
 * `buildWarmHolder` gathers, detached from the scene: a throwaway instance of every ship def (its look applied, the
 * engines and boost lit, a hull and a shield hit showing), a clone of every effects pool mesh (bolts, muzzle flashes,
 * sparks, explosions, trails) and of a torpedo, and clones of the travel effects (tunnel, streaks, the preview relay
 * core). Clones share their materials with the live objects, so their pipelines are the live ones. The throwaway ship
 * materials are never disposed: disposing them would release pipelines the live views share.
 *
 * M10 P1b3 (plan review M8): with ship streaming on, boot loads only the Kestrel among ship models, so a def whose
 * model isn't in warms as the stream's landed model or, failing that, as its band-sized stand-in in the def's faction
 * look. The faction ship-look pipelines (hull, lights, plumes, hits) therefore compile here, not when the
 * first raider shows. The stand-in comes straight from `standInModel` (the same cached model `ships.getOrStandIn`
 * hands out) so the warm-up starts no load: what streams is the traffic layer's and the hold's call, not every def.
 *
 * M10 P4b4c's boot kit slots (`addKitSlots`) skip when they look like their base (same model, livery and mount
 * modules: 12 of the 13 today), since the base's instance already compiles those pipelines. A slot with a new
 * module (the Harpoon gunship's torpedo racks in the raider look) still warms.
 */
import { Group, type Object3D } from 'three/webgpu'
import type { GameContext } from '../../app/feature'
import type { LoadedModel } from '../../assets/loader'
import type { ShipStream } from '../../assets/shipStream'
import { standInModel } from '../../assets/standIn'
import type { ModelEntry } from '../../data/assetSchema'
import type { ShipDef } from '../../data/schemas'
import { logger } from '../../core/logger'
import { FACTION_IDS } from '../../render/contracts'
import { applyShipLook, applyStaticLook } from '../../render/materials'
import { warmPipelines } from '../../render/reachFx/warmup'
import { kitBaseOf } from '../../ships/kitSlots'

/** Whatever the M1 effects feature exposes that can be cloned (its pools and torpedo views). */
export interface WarmFxSource {
  readonly system: { readonly list: readonly { readonly mesh: Object3D }[] } | null
  readonly torpedoes: { readonly free: Readonly<Record<string, readonly { readonly instance: { readonly root: Object3D } }[]>> } | null
}

/** The reach effects (tunnel, streaks, the preview core) to clone. */
export interface WarmReachFxSource {
  readonly state: { readonly tunnel: { readonly root: Object3D }; readonly streaks: { readonly root: Object3D }; readonly core: { readonly mesh: Object3D } | null } | null
}

function shownClone(o: Object3D): Object3D {
  const c = o.clone()
  c.traverse((x) => {
    x.visible = true
  })
  return c
}

/**
 * The model ship def asset `assetId` warms with (M10 P1b3): boot's, else the stream's landed one, else the stand-in
 * for its manifest entry; undefined only for an id with no manifest entry. Starts no load.
 */
export function warmShipModel(
  ctx: Pick<GameContext, 'models' | 'assets'>,
  ships: Pick<ShipStream, 'get'> | undefined,
  assetId: string,
): LoadedModel | undefined {
  const model = ctx.models.get(assetId) ?? ships?.get(assetId)
  if (model !== undefined) return model
  const entry: ModelEntry | undefined = ctx.assets.models.find((e) => e.id === assetId)
  return entry === undefined ? undefined : standInModel(entry)
}

/** Kit slot `i` draws as its base: same model, livery and mount modules (false for a base def). */
function looksLikeBase(defs: readonly ShipDef[], i: number): boolean {
  const b = kitBaseOf(defs, i)
  if (b < 0) return false
  const d = defs[i]
  const base = defs[b]
  return d.assetId === base.assetId && d.faction === base.faction && d.mounts.every((m) => base.mounts.some((x) => x.module === m.module))
}

/** A throwaway instance of every ship def but the kit slots that look like their base (a stand-in for one whose model hasn't streamed in), lit and hit, plus clones of the effects. */
export function buildWarmHolder(ctx: GameContext, fx: WarmFxSource | null, reachFx: WarmReachFxSource | null): Group {
  const holder = new Group()
  holder.name = 'reach:warm'
  const defs = ctx.sim.defs
  const ships = ctx.services.maybe('ships')
  for (let i = 0; i < defs.length; i++) {
    const def = defs[i]
    if (looksLikeBase(defs, i)) continue
    const model = warmShipModel(ctx, ships, def.assetId)
    if (model === undefined) continue
    const inst = model.instantiate(ctx.views.lodScale)
    const look = applyShipLook(ctx.lib, inst, def.faction)
    for (const mount of def.mounts) {
      const socket = inst.sockets.get(mount.socket)
      const mod = ctx.models.get(mount.module)
      if (socket === undefined || mod === undefined) continue
      const m = mod.instantiate(ctx.views.lodScale)
      applyStaticLook(ctx.lib, m, def.faction)
      socket.add(m.root)
    }
    look.setThrust(1, 1, true)
    look.setControl(1, 1, 1, 1, 1)
    look.hit(0, 0, 1, 1)
    look.shieldHit(0, 0, 1, 1)
    holder.add(inst.root)
  }
  const list = fx?.system?.list ?? []
  for (let i = 0; i < list.length; i++) holder.add(shownClone(list[i].mesh))
  const free = fx?.torpedoes?.free
  if (free !== undefined) {
    for (const f of FACTION_IDS) {
      const t = free[f]?.[0]
      if (t !== undefined) {
        holder.add(shownClone(t.instance.root))
        break
      }
    }
  }
  const s = reachFx?.state
  if (s) {
    holder.add(shownClone(s.tunnel.root), shownClone(s.streaks.root))
    if (s.core) holder.add(shownClone(s.core.mesh))
  }
  return holder
}

/** Builds the holder and compiles it in the real render contexts; never rejects. */
export async function warmReach(ctx: GameContext, fx: WarmFxSource | null, reachFx: WarmReachFxSource | null): Promise<void> {
  const t0 = performance.now()
  try {
    const holder = buildWarmHolder(ctx, fx, reachFx)
    const contexts = await warmPipelines(ctx.renderer, ctx.scene, ctx.camera, holder)
    logger.info(`reach: ${holder.children.length} ships and effects warmed in ${contexts} render contexts, ${(performance.now() - t0).toFixed(0)} ms`)
  } catch (err: unknown) {
    logger.warn('reach: the ship and effects warm-up failed; they compile on first draw', err)
  }
}
